/**
 * Product Detail screen with photo carousel, seller row, and bottom bar.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, Heart, Share2, BadgeCheck, Truck, MapPin, Send } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [product, setProduct] = useState(null);
  const [activePhoto, setActivePhoto] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [shareSheet, setShareSheet] = useState(false);
  const [shareText, setShareText] = useState("");
  const [shareBusy, setShareBusy] = useState(false);

  // Persist trainer_ref (if any) so "Add to Cart" credits the recommending pro
  useEffect(() => {
    const ref = searchParams.get("trainer_ref");
    if (ref) {
      try {
        sessionStorage.setItem("ff_trainer_ref", ref);
      } catch {
        /* ignore */
      }
    }
  }, [searchParams]);

  useEffect(() => {
    apiClient.get(`/marketplace/products/${id}`).then((r) => setProduct(r.data)).catch(() => setProduct(null));
  }, [id]);

  useEffect(() => {
    if (product?.seller_id) {
      apiClient.get(`/marketplace/reviews/seller/${product.seller_id}`).then((r) => setReviews(r.data)).catch(() => {});
    }
  }, [product?.seller_id]);

  const toggleSave = async () => {
    if (!user) return navigate("/login");
    try {
      const { data } = await apiClient.post("/marketplace/saves/toggle", { product_id: product.product_id });
      setProduct({ ...product, is_saved: data.saved });
    } catch { toast.error("Could not save"); }
  };

  const addToCart = async () => {
    if (!user) return navigate("/login");
    try {
      let trainerRef = null;
      try {
        trainerRef = sessionStorage.getItem("ff_trainer_ref");
      } catch {
        /* ignore */
      }
      await apiClient.post("/marketplace/cart", {
        product_id: product.product_id,
        quantity: 1,
        ...(trainerRef ? { trainer_ref: trainerRef } : {}),
      });
      toast.success("Added to cart");
      navigate("/marketplace/cart");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };

  const shareToCommunity = async () => {
    if (!user) return navigate("/login");
    const text = shareText.trim() || `Check out ${product.title}`;
    setShareBusy(true);
    try {
      await apiClient.post("/feed/posts", {
        text: text.slice(0, 280),
        hub: "all",
        attached_product_id: product.product_id,
      });
      toast.success("Shared to Community");
      setShareSheet(false);
      setShareText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not share");
    } finally {
      setShareBusy(false);
    }
  };

  const messageSeller = async () => {
    if (!user) return navigate("/login");
    try {
      const { data } = await apiClient.post("/marketplace/threads", {
        product_id: product.product_id,
      });
      navigate(`/inbox?tab=marketplace&thread=${data.thread_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open thread");
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/marketplace/product/${product.product_id}`;
    if (navigator.share) {
      try { await navigator.share({ title: product.title, text: `${product.title} — ₹${product.price_inr}`, url }); } catch {}
    } else {
      try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch {}
    }
  };

  if (!product) return <div className="p-10 text-center text-ink-soft">Loading…</div>;

  const discount = product.original_price_inr && product.original_price_inr > product.price_inr;
  const savings = discount ? product.original_price_inr - product.price_inr : 0;
  const soldOut = product.status === "sold" || (product.stock_count || 0) === 0;

  return (
    <div className="app-page min-h-screen pb-[96px]" data-testid="product-detail" style={{ background: "#F4F3F1" }}>
      {/* Top bar */}
      <header className="sticky top-0 z-20 bg-canvas/85 backdrop-blur-xl">
        <div className="max-w-2xl mx-auto h-14 px-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} aria-label="Back" data-testid="detail-back" className="w-10 h-10 grid place-items-center rounded-full hover:bg-white/60"><ChevronLeft size={22} /></button>
          <div className="flex items-center gap-1">
            <button onClick={() => setShareSheet(true)} aria-label="Share to Community" data-testid="share-to-community" className="w-10 h-10 grid place-items-center rounded-full hover:bg-white/60"><Send size={18} /></button>
            <button onClick={share} aria-label="Share" data-testid="detail-share" className="w-10 h-10 grid place-items-center rounded-full hover:bg-white/60"><Share2 size={18} /></button>
            <button onClick={toggleSave} aria-label="Save" data-testid="detail-save" className="w-10 h-10 grid place-items-center rounded-full hover:bg-white/60">
              <Heart size={18} fill={product.is_saved ? "#00D4FF" : "transparent"} color={product.is_saved ? "#00D4FF" : "#1A1A1A"} />
            </button>
          </div>
        </div>
      </header>

      {/* Photo carousel */}
      <div className="relative bg-line2" style={{ height: 300 }} data-testid="photo-carousel">
        <div className="flex overflow-x-auto snap-x snap-mandatory h-full" style={{ scrollbarWidth: "none" }} onScroll={(e) => { const i = Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth); setActivePhoto(i); }}>
          {(product.photos || []).map((p, i) => (
            <img key={i} src={p} alt="" className="h-full w-full object-cover shrink-0 snap-center" />
          ))}
        </div>
        {(product.photos || []).length > 1 && (
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
            {product.photos.map((_, i) => (
              <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: i === activePhoto ? "#00D4FF" : "rgba(255,255,255,0.6)" }} />
            ))}
          </div>
        )}
      </div>

      <main className="max-w-2xl mx-auto px-4 py-4">
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-tertiary">{product.category.replace("_", " ")}</span>
        <h1 className="font-barlow-cond font-black mt-1" style={{ fontSize: 28, lineHeight: 1.05, color: "#1A1A1A" }} data-testid="detail-title">{product.title}</h1>
        {product.condition && (
          <span className="inline-block mt-2 px-2 h-6 leading-6 text-[10px] font-bold uppercase rounded-pill" style={{ background: "#ECEAE7", borderRadius: 100 }}>
            {{new:"New", like_new:"Like New", good:"Good", fair:"Fair"}[product.condition]}
          </span>
        )}

        {/* Price */}
        <div className="flex items-baseline gap-2.5 mt-4" data-testid="detail-price">
          <span className="font-barlow-cond font-black" style={{ fontSize: 36, color: "#1A1A1A", lineHeight: 1 }}>₹{product.price_inr.toLocaleString("en-IN")}</span>
          {discount && (
            <>
              <span className="text-[13px] text-ink-tertiary line-through">₹{product.original_price_inr.toLocaleString("en-IN")}</span>
              <span className="inline-block px-2.5 h-6 leading-6 text-[12px] font-bold rounded-pill" style={{ background: "rgba(0,180,100,0.10)", color: "rgba(0,150,80,1)", borderRadius: 100 }}>Save ₹{savings.toLocaleString("en-IN")}</span>
            </>
          )}
        </div>

        {/* Seller row */}
        <div className="mt-4 flex items-center gap-3 bg-white border border-line2 rounded-[12px] p-3" data-testid="detail-seller">
          <div className="w-9 h-9 rounded-full overflow-hidden bg-canvas2 grid place-items-center">
            {product.seller_avatar ? <img src={product.seller_avatar} alt="" className="w-full h-full object-cover" /> : <span className="text-[13px] font-semibold">{(product.seller_name || "?")[0]}</span>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-bold text-[14px] truncate">{product.seller_name || "Seller"}</span>
              {product.seller_type === "brand" && <BadgeCheck size={14} style={{ color: "#00B0D5" }} />}
            </div>
            <div className="text-[12px] text-ink-tertiary">{product.city}</div>
            {product.review_count > 0 && (
              <div className="text-[11.5px] text-ink-soft mt-0.5">★ {product.avg_rating} · {product.review_count} review{product.review_count > 1 ? "s" : ""}</div>
            )}
          </div>
        </div>

        {/* Delivery tags */}
        <div className="mt-3 flex gap-2 flex-wrap">
          {product.delivery_available && (
            <span className="inline-flex items-center gap-1 px-3 h-8 text-[12px] font-medium rounded-pill bg-canvas2 border border-line2" style={{ borderRadius: 100 }}><Truck size={12} /> Delivery available</span>
          )}
          {product.pickup_only && (
            <span className="inline-flex items-center gap-1 px-3 h-8 text-[12px] font-medium rounded-pill bg-canvas2 border border-line2" style={{ borderRadius: 100 }}><MapPin size={12} /> Pickup from {product.city}</span>
          )}
        </div>

        {/* Description */}
        <section className="mt-5" data-testid="detail-description">
          <h2 className="font-barlow-cond font-bold text-[18px]">About this item</h2>
          <p className="text-[14px] text-ink-soft mt-1.5" style={{ lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{product.description}</p>
        </section>

        {/* Reviews */}
        {reviews.length > 0 && (
          <section className="mt-6" data-testid="detail-reviews">
            <h2 className="font-barlow-cond font-bold text-[18px] mb-2">Reviews</h2>
            {reviews.slice(0, 3).map((r) => (
              <div key={r.review_id} className="bg-white border border-line2 rounded-[12px] p-3 mb-2">
                <div className="flex items-center justify-between">
                  <span className="text-[13px] font-semibold">{r.reviewer_name}</span>
                  <span className="text-[12px]">{"★".repeat(r.rating)}<span className="text-ink-tertiary">{"★".repeat(5 - r.rating)}</span></span>
                </div>
                <p className="text-[13px] text-ink-soft mt-1">{r.review_text}</p>
              </div>
            ))}
          </section>
        )}
      </main>

      {/* Fixed bottom bar */}
      <div className="fixed left-0 right-0 bottom-0 z-30" style={{ background: "rgba(255,255,255,0.92)", backdropFilter: "blur(20px)", borderTop: "1px solid #E2E0DC", padding: "12px 16px", paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }} data-testid="detail-action-bar">
        <div className="max-w-2xl mx-auto flex gap-2.5">
          {soldOut ? (
            <button disabled className="w-full h-12 rounded-pill font-bold" style={{ background: "#ECEAE7", color: "#8E8A84", borderRadius: 100 }}>Sold Out</button>
          ) : (
            <>
              <button onClick={messageSeller} data-testid="msg-seller" className="flex-[1.1] h-12 rounded-pill font-semibold border border-line2" style={{ borderRadius: 100 }}>Message Seller</button>
              <button onClick={addToCart} data-testid="detail-add-cart" className="flex-[1.3] h-12 rounded-pill font-bold" style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}>Add to Cart</button>
            </>
          )}
        </div>
      </div>
      {/* Share-to-Community sheet */}
      {shareSheet && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-end"
          onClick={() => setShareSheet(false)}
          data-testid="share-community-sheet"
        >
          <div
            className="w-full bg-white rounded-t-[24px] p-5 max-w-2xl mx-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-barlow-cond font-extrabold text-[22px]">Share to Community</h3>
            <p className="text-[13px] text-ink-soft mt-1">
              Your post will include a product card for {product.title}.
            </p>
            <textarea
              value={shareText}
              onChange={(e) => setShareText(e.target.value.slice(0, 280))}
              placeholder={`What do you love about ${product.title}?`}
              data-testid="share-community-text"
              className="mt-3 w-full p-3 rounded-[12px] border border-line bg-canvas2 outline-none"
              style={{ minHeight: 90 }}
            />
            <div className="text-right text-[11px] text-ink-soft">{shareText.length}/280</div>
            <div className="flex gap-2.5 mt-3">
              <button
                onClick={() => setShareSheet(false)}
                className="flex-1 h-12 rounded-pill border border-line2"
                style={{ borderRadius: 100 }}
              >
                Cancel
              </button>
              <button
                onClick={shareToCommunity}
                disabled={shareBusy}
                data-testid="share-community-submit"
                className="flex-1 h-12 rounded-pill disabled:opacity-50"
                style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
              >
                {shareBusy ? "Posting…" : "Post"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
